import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Chip, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import moment from "moment";
import IMasterClass from "../../types/MasterClass";
import IActividad from "../../types/Actividad";
import ISala from "../../types/Sala";
import ClaseUtils from "../../utils/Clase";
import ConfigUtils from "../../utils/Config";
import * as constantes from "../../common/Constantes";

// Selector de clases recurrentes, compartido por «Modificar pacientes», «Editar clases» y «Eliminar clases»: una lista por
// actividad, ordenada por día y hora, con especialista, sala, fecha final y ocupación de cada clase.
type SelectorClasesProps = {
    masterClasses: IMasterClass[],
    sesionesPorClase?: Record<string, Date[]>, // fechas de las sesiones de cada clase (para su primera clase real)
    checked: string[],
    onChange: (checked: string[]) => void
}

const hora = (master: IMasterClass) => moment(master.start).format('H:mm');
const diaYHora = (master: IMasterClass) => `${constantes.weekNames[master.weekDay]} ${hora(master)}`;

// Primera sesión real de la serie (sin festivos); si no hay sesiones cargadas, el inicio guardado de la clase
const primeraClase = (master: IMasterClass, sesiones?: Date[]): Date =>
    ClaseUtils.primeraSesion(sesiones, new Date(master.start)) ?? new Date(master.start);

// La clase aún no ha empezado: su primera clase es posterior a hoy
const empiezaMasAdelante = (master: IMasterClass, sesiones?: Date[]) => moment(primeraClase(master, sesiones)).isAfter(moment(), 'day');

// Pacientes distintos apuntados (un paciente con varios tramos cuenta una vez) hoy o, si la clase aún no ha empezado, en su
// primera clase: si no, una clase que empieza más adelante saldría siempre vacía aunque ya tenga gente apuntada
const ocupados = (master: IMasterClass, sesiones?: Date[]): number => {
    const ref = empiezaMasAdelante(master, sesiones) ? moment(primeraClase(master, sesiones)) : moment();
    const dia = moment.utc({ year: ref.year(), month: ref.month(), date: ref.date() }); // día local como día UTC (así se guardan los tramos)
    return new Set(master.pacientes
        .filter(p => moment.utc(p.start).startOf('day').isSameOrBefore(dia) && moment.utc(p.end).endOf('day').isSameOrAfter(dia))
        .map(p => p.id)).size;
};

const SelectorClases = ({ masterClasses, sesionesPorClase, checked, onChange }: SelectorClasesProps) => {
    const actividades: IActividad[] = ConfigUtils.getActividades();
    const salas: ISala[] = ConfigUtils.getSalas();
    const empleados = ConfigUtils.getEmpleados();

    const toggle = (id: string) => onChange(checked.includes(id) ? checked.filter(c => c !== id) : [...checked, id]);
    const anadir = (ids: string[]) => onChange([...checked, ...ids.filter(id => !checked.includes(id))]);

    // Clases agrupadas por actividad y ordenadas por día y hora
    const grupos = masterClasses.reduce((acc, master) => {
        const titulo = ClaseUtils.actividadDe(actividades, master.title)?.title ?? 'Sin actividad';
        if (!acc[titulo]) acc[titulo] = [];
        acc[titulo].push(master);
        return acc;
    }, {} as Record<string, IMasterClass[]>);
    Object.values(grupos).forEach(lista => lista.sort((a, b) => a.weekDay - b.weekDay || moment(a.start).format('HH:mm').localeCompare(moment(b.start).format('HH:mm'))));

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Puedes elegir varias, de una o varias actividades</Typography>

            {/* Clases elegidas */}
            {checked.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {checked.map(id => {
                    const master = masterClasses.find(m => m._id === id);
                    if (!master) return null;
                    return (
                        <Chip
                            key={id}
                            label={`${ClaseUtils.actividadDe(actividades, master.title)?.title ?? ''} · ${diaYHora(master)}`}
                            onDelete={() => toggle(id)}
                            color="primary"
                            variant="outlined"
                        />
                    );
                })}
            </Box>}

            {Object.entries(grupos).map(([titulo, clases]) => {
                const elegidas = clases.filter(m => checked.includes(m._id!)).length;
                return (
                    <Accordion key={titulo} defaultExpanded={elegidas > 0 || Object.keys(grupos).length === 1} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                                <Typography sx={{ fontWeight: 500 }}>{titulo}</Typography>
                                <Typography variant="body2" color={elegidas > 0 ? 'primary' : 'text.secondary'}>
                                    {elegidas > 0 ? `${elegidas} ${elegidas === 1 ? 'elegida' : 'elegidas'}` : `${clases.length} ${clases.length === 1 ? 'clase' : 'clases'}`}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            {clases.map(master => {
                                const id = master._id!;
                                const elegida = checked.includes(id);
                                const capacidad = ClaseUtils.actividadDe(actividades, master.title)?.capacity ?? 0;
                                const sesiones = sesionesPorClase?.[id];
                                const apuntados = ocupados(master, sesiones);
                                const completa = capacidad > 0 && apuntados >= capacidad;
                                const especialista = empleados.find(e => e.internalID === master.especialistaId)?.firstName;
                                const sala = salas.find(s => String(s.internalID) === String(master.salaId))?.title;
                                const fin = moment(master.start).add(master.duracion, 'minutes').format('H:mm');
                                // Pareja sin elegir (creada junto con esta): se sugiere añadirla, nunca se marca sola
                                const parejasSinElegir = elegida ? ClaseUtils.parejasDe(master, masterClasses).filter(p => !checked.includes(p._id!)) : [];
                                const diasPareja = parejasSinElegir.map(p => constantes.weekNames[p.weekDay].toLowerCase());
                                const textoPareja = diasPareja.length === 1
                                    ? `Añadir también la del ${diasPareja[0]} ${hora(master)}`
                                    : `Añadir también las del ${diasPareja.slice(0, -1).join(', ')} y ${diasPareja[diasPareja.length - 1]} ${hora(master)}`;

                                return (
                                    <Box key={id} sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: elegida ? 'action.selected' : 'transparent' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.5 }}>
                                            <Checkbox checked={elegida} onChange={() => toggle(id)} size="small" />
                                            <Box sx={{ flex: 1, py: 0.75, cursor: 'pointer' }} onClick={() => toggle(id)}>
                                                <Typography variant="body2">{`${constantes.weekNames[master.weekDay]} ${hora(master)} a ${fin}`}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {[especialista, sala, `hasta ${moment(master.terminarRepetir).format('DD/MM/YYYY')}`].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                    {empiezaMasAdelante(master, sesiones) && (
                                                        <Chip label={`empieza ${moment(primeraClase(master, sesiones)).format('DD/MM/YYYY')}`} size="small" color="info" variant="outlined" sx={{ height: 20 }} />
                                                    )}
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" sx={{ py: 0.75, whiteSpace: 'nowrap', color: completa ? 'warning.main' : 'text.secondary' }}>
                                                {`${apuntados}/${capacidad}${completa ? ' completa' : ''}`}
                                            </Typography>
                                        </Box>
                                        {parejasSinElegir.length > 0 && (
                                            <Box sx={{ pl: 6, pb: 0.5 }}>
                                                <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={() => anadir(parejasSinElegir.map(p => p._id!))}>
                                                    {textoPareja}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );
};

export default SelectorClases;
